import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useAuth } from './contexts/AuthContext';
import { canAccessPath, isCoAdmin } from './config/rolePermissions';
import { getActiveCompanyId } from './utils/activeCompany';

// Layout
import Layout from './components/Layout/Layout';
import PlatformAdminLayout from './components/PlatformAdmin/PlatformAdminLayout';
import ErrorBoundary from './components/ErrorBoundary';

/* T269 / T262 — a lazily loaded page whose chunk cannot be fetched (the classic case: a
   tab opened BEFORE a redeploy asks for a chunk name that no longer exists, and the SPA
   fallback answers with index.html) would otherwise leave the app suspended on a promise
   that never resolves — every state update queued behind it, the header menu frozen open.
   On the first failure the page reloads once, which fetches the current index.html and
   the current chunk names; a second failure in the same session is a real error and
   reaches the ErrorBoundary. */
const lazyRetry = (factory: () => Promise<{ default: React.ComponentType<any> }>) =>
  React.lazy(() =>
    factory().then((m) => { sessionStorage.removeItem('forge-chunk-reload'); return m; }).catch((err) => {
      if (!sessionStorage.getItem('forge-chunk-reload')) {
        sessionStorage.setItem('forge-chunk-reload', '1');
        window.location.reload();
        return new Promise<never>(() => {}); // the reload takes over
      }
      throw err;
    }),
  );

/* T262 — *"THE APP IS LOADING TOO MUCH IN THE WEB"*. Measured, not guessed: ONE
   3.58 MB bundle (904 KB over the wire, brotli) and ZERO lazy routes, so every one
   of the 45 pages was parsed before the login screen could paint. Each page below
   is now its own chunk, fetched the first time its route is visited. Layout, the
   two shells and LoginPage stay eager: they ARE the first paint. */

// Platform Admin Pages
const CDPLPlatformDashboardPage = lazyRetry(() => import('./pages/platform-admin/CDPLPlatformDashboardPage'));
const PlatformCompaniesPage = lazyRetry(() => import('./pages/platform-admin/PlatformCompaniesPage'));
const PlatformUsersPage = lazyRetry(() => import('./pages/platform-admin/PlatformUsersPage'));
const PlatformSettingsPage = lazyRetry(() => import('./pages/platform-admin/PlatformSettingsPage'));
const CDPLAdminsPage = lazyRetry(() => import('./pages/platform-admin/CDPLAdminsPage'));
const CDPLTeamsPage = lazyRetry(() => import('./pages/platform-admin/CDPLTeamsPage'));
const CDPLActivityLogsPage = lazyRetry(() => import('./pages/platform-admin/CDPLActivityLogsPage'));
const CDPLAuditLogsPage = lazyRetry(() => import('./pages/platform-admin/CDPLAuditLogsPage'));
const CDPLNotificationsPage = lazyRetry(() => import('./pages/platform-admin/CDPLNotificationsPage'));
const PlatformReportsPage = lazyRetry(() => import('./pages/platform-admin/PlatformReportsPage'));
const PlatformInsightsPage = lazyRetry(() => import('./pages/platform-admin/PlatformInsightsPage'));
const CDPLSubscriptionsPage = lazyRetry(() => import('./pages/platform-admin/CDPLSubscriptionsPage'));
const CDPLBillingPage = lazyRetry(() => import('./pages/platform-admin/CDPLBillingPage'));
const CDPLIntegrationsPage = lazyRetry(() => import('./pages/platform-admin/CDPLIntegrationsPage'));
const CDPLApiKeysPage = lazyRetry(() => import('./pages/platform-admin/CDPLApiKeysPage'));
const CDPLRecycleBinPage = lazyRetry(() => import('./pages/platform-admin/CDPLRecycleBinPage'));
const CDPLCompanyOwnersPage = lazyRetry(() => import('./pages/platform-admin/CDPLCompanyOwnersPage'));
const PlatformAccessControlPage = lazyRetry(() => import('./pages/platform-admin/PlatformAccessControlPage'));
const PlatformRolesPermissionsPage = lazyRetry(() => import('./pages/platform-admin/PlatformRolesPermissionsPage'));

// Pages
import LoginPage from './pages/LoginPage';
const ForgotPasswordPage = lazyRetry(() => import('./pages/ForgotPasswordPage'));
const DashboardPage = lazyRetry(() => import('./pages/DashboardPage'));
const ProjectsPage = lazyRetry(() => import('./pages/ProjectsPage'));
const ProjectDetailPage = lazyRetry(() => import('./pages/ProjectDetailPage'));
const ClientsPage = lazyRetry(() => import('./pages/ClientsPage'));
const VendorsPage = lazyRetry(() => import('./pages/VendorsPage'));
const SettingsPage = lazyRetry(() => import('./pages/SettingsPage'));
const AccessControlPage = lazyRetry(() => import('./pages/AccessControlPage'));
const AddVendorPage = lazyRetry(() => import('./pages/AddVendorPage'));
const BusinessAnalyticsPage = lazyRetry(() => import('./pages/BusinessAnalyticsPage'));
// Material Master removed — Parts Master is the single source of truth
const PartsMasterPage = lazyRetry(() => import('./pages/PartsMasterPage'));
const RawMaterialMasterPage = lazyRetry(() => import('./pages/RawMaterialMasterPage'));
const StandardsPage = lazyRetry(() => import('./pages/StandardsPage')); // T292
const VendorPOPage = lazyRetry(() => import('./pages/VendorPOPage'));
const VendorProcurementPage = lazyRetry(() => import('./pages/VendorProcurementPage'));
const VendorDetailPage = lazyRetry(() => import('./pages/VendorDetailPage'));
const VendorEditPage = lazyRetry(() => import('./pages/VendorEditPage'));
const ClientDetailPage = lazyRetry(() => import('./pages/ClientDetailPage'));
const ClientEditPage = lazyRetry(() => import('./pages/ClientEditPage'));
const RecycleBinPage = lazyRetry(() => import('./pages/RecycleBinPage'));
const MaterialStockPage = lazyRetry(() => import('./pages/MaterialStockPage'));
const ProcurementPage = lazyRetry(() => import('./pages/ProcurementPage'));
const MgmtProcurementPage = lazyRetry(() => import('./pages/MgmtProcurementPage'));
const FileManagerPage = lazyRetry(() => import('./pages/FileManagerPage'));
const UsersPage = lazyRetry(() => import('./pages/UsersPage'));

// Enterprise Components
const SessionMonitoring = lazyRetry(() => import('./components/SessionMonitoring'));
const CustomRoleBuilder = lazyRetry(() => import('./components/CustomRoleBuilder'));
const ApprovalWorkflowUI = lazyRetry(() => import('./components/ApprovalWorkflowUI'));
const RiskDashboard = lazyRetry(() => import('./components/RiskDashboard'));
const ActivityTimelineView = lazyRetry(() => import('./components/ActivityTimelineView'));
const ChatPage = lazyRetry(() => import('./pages/ChatPage'));

// Loading Component
const LoadingScreen: React.FC = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      background: '#F9FAFB',
    }}
  >
    <CircularProgress />
  </Box>
);

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

/**
 * RBAC-aware route guard. Uses canAccessPath with co-admin awareness
 * to enforce visibility rules:
 * - Owner/Co-Owner → full access
 * - Super Admin → no Administration, Enterprise only Risk Dashboard
 * - Admin → no Administration, no Enterprise
 * - User → restricted routes, no Administration, no Enterprise
 */
const RBACRoute: React.FC<{ children: React.ReactNode; path: string }> = ({ children, path }) => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/" replace />;
  const userIsCoAdmin = user.is_co_admin ?? isCoAdmin(user.role, user.is_co_admin);
  if (!canAccessPath(user.role, path, userIsCoAdmin)) return <Navigate to="/" replace />;
  return <>{children}</>;
};

// Co Admin route guard: Owner/Co-Owner/Super Admin (all main_admin)
const CoAdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/" replace />;
  if (user.role !== 'main_admin') return <Navigate to="/" replace />;
  return <>{children}</>;
};

// Platform Admin route guard: only platform_admin role
const PlatformAdminGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'platform_admin') return <Navigate to="/" replace />;
  return <>{children}</>;
};

const App: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const activeCompanyId = getActiveCompanyId();

  // Determine where to redirect after login based on role
  const homeRoute = user?.role === 'platform_admin'
    ? (activeCompanyId ? '/' : '/platform-admin')
    : '/';

  return (
    <ErrorBoundary>
      {/* T262 — the fallback while a route's chunk downloads. Same screen the auth
          gate shows, so a slow chunk looks like a load, not a blank page. */}
      <React.Suspense fallback={<LoadingScreen />}>
      <Routes>
        <Route 
          path="/login" 
          element={isAuthenticated ? <Navigate to={homeRoute} replace /> : <LoginPage />} 
        />
         <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              {user?.role === 'platform_admin' && !activeCompanyId ? <Navigate to="/platform-admin" replace /> : <Layout />}
            </ProtectedRoute>
          }
        >
          {/* ══════════════════════════════════════════════════════════════════
              T99 — THE APP LANDS ON PROJECTS, NOT THE DASHBOARD.

              Owner: "when the page is loaded, make the default 1st page as project
              screen instead of dashboard."

              A REDIRECT, NOT A SWAPPED ELEMENT. Rendering <ProjectsPage> here would
              show Projects at the address "/", and `Layout.isActive` special-cases
              "/" as the Dashboard — so the sidebar would highlight Dashboard while
              the Projects list was on screen, and a bookmark would record the wrong
              address. Moving the dashboard to its own path keeps the URL honest
              about what is being shown.

              This also re-points every OTHER route that falls back to "/" — the RBAC
              denial, the 404 catch-all, the logo — at Projects, which is the same
              answer to "where does this app start". */}
          <Route index element={<Navigate to="/projects" replace />} />
          {/* The dashboard is MOVED, not removed. Ungated exactly as it was as the
              index route; the sidebar link follows it, and `/dashboard` is added to
              the `user` role's allowed routes so a later gate cannot silently hide
              a page that every role can reach today. */}
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="proposals" element={<ProjectsPage phase="proposal" />} />
          <Route path="proposals/:id" element={<ErrorBoundary><ProjectDetailPage /></ErrorBoundary>} />
          <Route path="projects" element={<ProjectsPage phase="project" />} />
          <Route path="projects/:id" element={<ErrorBoundary><ProjectDetailPage /></ErrorBoundary>} />
          <Route path="clients" element={<RBACRoute path="/clients"><ClientsPage /></RBACRoute>} />
          <Route path="clients/:id" element={<RBACRoute path="/clients"><ClientDetailPage /></RBACRoute>} />
          <Route path="clients/:id/edit" element={<RBACRoute path="/clients"><ClientEditPage /></RBACRoute>} />
          <Route path="vendors" element={<RBACRoute path="/vendors"><VendorsPage /></RBACRoute>} />
          <Route path="vendors/add" element={<RBACRoute path="/vendors"><AddVendorPage /></RBACRoute>} />
          <Route path="vendors/:id" element={<RBACRoute path="/vendors"><VendorDetailPage /></RBACRoute>} />
          <Route path="vendors/:id/edit" element={<RBACRoute path="/vendors"><VendorEditPage /></RBACRoute>} />
          <Route path="users" element={<RBACRoute path="/users"><UsersPage /></RBACRoute>} />
          <Route path="vendor-po" element={<RBACRoute path="/vendor-po"><VendorPOPage /></RBACRoute>} />
          <Route path="vendor-procurement" element={<RBACRoute path="/vendor-procurement"><VendorProcurementPage /></RBACRoute>} />
          <Route path="material-stock" element={<RBACRoute path="/material-stock"><MaterialStockPage /></RBACRoute>} />
          <Route path="raw-materials" element={<RBACRoute path="/raw-materials"><RawMaterialMasterPage /></RBACRoute>} />
          <Route path="standards" element={<RBACRoute path="/standards"><StandardsPage /></RBACRoute>} />
          <Route path="parts-master" element={<RBACRoute path="/parts-master"><PartsMasterPage /></RBACRoute>} />
          <Route path="procurement" element={<RBACRoute path="/procurement"><MgmtProcurementPage /></RBACRoute>} />
          <Route path="file-manager" element={<FileManagerPage />} />
          {/* Access Control moved to /platform-admin/access-control */}
          <Route path="access-control" element={<Navigate to="/platform-admin/access-control/users" replace />} />
          <Route path="access-control/*" element={<Navigate to="/platform-admin/access-control/users" replace />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="recycle-bin" element={<CoAdminRoute><RecycleBinPage /></CoAdminRoute>} />
          {/* Enterprise routes — RBAC enforced (Super Admin: only risk-dashboard) */}
          <Route path="sessions" element={<RBACRoute path="/sessions"><SessionMonitoring /></RBACRoute>} />
          <Route path="custom-roles" element={<RBACRoute path="/custom-roles"><CustomRoleBuilder /></RBACRoute>} />
          <Route path="approvals" element={<RBACRoute path="/approvals"><ApprovalWorkflowUI /></RBACRoute>} />
          <Route path="risk-dashboard" element={<RBACRoute path="/risk-dashboard"><RiskDashboard /></RBACRoute>} />
          {/* Other routes */}
          <Route path="business-analytics" element={<RBACRoute path="/business-analytics"><BusinessAnalyticsPage /></RBACRoute>} />
          <Route path="analytics" element={<RBACRoute path="/analytics"><BusinessAnalyticsPage /></RBACRoute>} />
          <Route path="activity-timeline" element={<RBACRoute path="/activity-timeline"><ActivityTimelineView /></RBACRoute>} />
          <Route path="messages" element={<ChatPage />} />
        </Route>

        {/* Platform Admin Routes — separate layout, platform_admin role only */}
        <Route
          path="/platform-admin"
          element={
            <ProtectedRoute>
              <PlatformAdminGuard>
                <PlatformAdminLayout />
              </PlatformAdminGuard>
            </ProtectedRoute>
          }
        >
          <Route index element={<CDPLPlatformDashboardPage />} />
          <Route path="companies" element={<PlatformCompaniesPage />} />
          <Route path="company-owners" element={<CDPLCompanyOwnersPage />} />
          <Route path="users" element={<PlatformUsersPage />} />
          <Route path="admins" element={<CDPLAdminsPage />} />
          <Route path="teams" element={<CDPLTeamsPage />} />
          <Route path="activity-logs" element={<CDPLActivityLogsPage />} />
          <Route path="audit-logs" element={<CDPLAuditLogsPage />} />
          <Route path="notifications" element={<CDPLNotificationsPage />} />
          <Route path="reports" element={<PlatformReportsPage />} />
          <Route path="insights" element={<PlatformInsightsPage />} />
          <Route path="subscriptions" element={<CDPLSubscriptionsPage />} />
          <Route path="billing" element={<CDPLBillingPage />} />
          <Route path="integrations" element={<CDPLIntegrationsPage />} />
          <Route path="api-keys" element={<CDPLApiKeysPage />} />
          <Route path="settings" element={<PlatformSettingsPage />} />
          <Route path="recycle-bin" element={<CDPLRecycleBinPage />} />
          {/* Access Control — Company-based user management */}
          <Route path="access-control" element={<PlatformAccessControlPage />} />
          <Route path="access-control/roles" element={<PlatformRolesPermissionsPage />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </React.Suspense>
    </ErrorBoundary>
  );
};

export default App;
